import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Loader2, Check, ShieldCheck, Heart, ClipboardList } from "lucide-react";
import axios from "axios";
import { toast, Toaster } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full bg-cream border border-sand rounded-xl px-3.5 py-3 text-ink focus:outline-none focus:border-navy focus:ring-1 focus:ring-navy text-[15px] font-sans";
const labelCls = "block text-[11px] uppercase tracking-[0.2em] text-ink-soft mb-1.5";

function Section({ icon: Icon, title, subtitle, children }) {
  return (
    <section className="bg-cream-soft border border-sand/70 rounded-3xl p-6 md:p-8 mb-5">
      <div className="flex items-center gap-3 mb-1">
        <Icon size={18} className="text-navy" />
        <h2 className="font-serif text-2xl text-ink">{title}</h2>
      </div>
      {subtitle && <p className="text-sm text-ink-soft mb-5 italic">{subtitle}</p>}
      <div className="space-y-4">{children}</div>
    </section>
  );
}

export default function Intake() {
  const { token } = useParams();
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [intake, setIntake] = useState({
    full_legal_name: "",
    preferred_name: "",
    date_of_birth: "",
    address: "",
    phone: "",
    email: "",
    occupation: "",
    emergency_contact: { name: "", relationship: "", phone: "" },
    primary_reasons: "",
    duration: "",
    goals: "",
    distress_level: 5,
    mental_health_history: "",
    prior_therapy: "",
    medications: "",
    other_treatment: "",
    physical_health: "",
    substance_use: "",
  });
  const [agreement, setAgreement] = useState({
    typed_signature: "",
    agreed_to_collaboration: false,
    agreed_to_process: false,
    agreed_to_confidentiality: false,
    agreed_to_late_fees_policy: false,
    signed_on: new Date().toISOString().slice(0, 10),
  });

  useEffect(() => {
    axios
      .get(`${API}/intake/${token}`)
      .then((r) => {
        setMeta(r.data);
        if (r.data.status === "submitted") setDone(true);
        setIntake((x) => ({ ...x, email: r.data.client_email || "", full_legal_name: r.data.client_name || "" }));
      })
      .catch(() => setMeta({ error: true }))
      .finally(() => setLoading(false));
  }, [token]);

  const submitReConsent = async (e) => {
    e.preventDefault();
    if (!agreement.agreed_to_late_fees_policy) {
      toast.error("Please tick the box to acknowledge the updated late-fees policy.");
      return;
    }
    if (!agreement.typed_signature.trim()) {
      toast.error("Please type your full name as your signature.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/intake/${token}/re-consent`, {
        typed_signature: agreement.typed_signature.trim(),
        signed_on: agreement.signed_on,
        agreed_to_late_fees_policy: true,
      });
      toast.success("Thank you — your acknowledgement is with Jesse.");
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not submit — please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!agreement.agreed_to_collaboration || !agreement.agreed_to_process || !agreement.agreed_to_confidentiality || !agreement.agreed_to_late_fees_policy) {
      toast.error("Please tick all four agreement boxes to continue.");
      return;
    }
    if (!agreement.typed_signature.trim()) {
      toast.error("Please type your full name as your signature.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/intake/${token}`, { intake, agreement });
      toast.success("Thank you — your forms are with Jesse.");
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not submit — please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center text-ink-soft">
        <Loader2 className="animate-spin mr-2" /> Loading your forms…
      </div>
    );
  }

  if (meta?.error) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center px-6">
        <div className="max-w-md text-center">
          <h1 className="font-serif text-3xl text-ink mb-2">Link not found</h1>
          <p className="text-ink-soft">This intake link is invalid or has expired. Please check the most recent email from Jesse, or reply to it for a fresh link.</p>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div className="min-h-screen bg-cream px-6 py-16">
        <div className="max-w-2xl mx-auto text-center">
          <div className="inline-flex items-center justify-center h-14 w-14 rounded-full bg-navy text-cream mb-5">
            <Check size={26} />
          </div>
          <h1 className="font-serif text-4xl text-ink mb-3">Thank you.</h1>
          <p className="text-ink-soft text-lg leading-relaxed">
            Your welcome and counselling agreement forms have come through safely. Jesse will review them before your session.
          </p>
          <p className="text-ink-soft mt-4">
            We've also emailed you a copy of our cancellation policy for your records. If you don't see it, please check your spam folder.
          </p>
        </div>
      </div>
    );
  }

  if (meta?.re_consent && !done) {
    return (
      <div className="min-h-screen bg-cream px-4 md:px-6 py-10">
        <Toaster richColors position="top-center" />
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-8">
            <div className="text-[11px] uppercase tracking-[0.28em] text-ink-soft mb-3">A Mindful Space Counselling</div>
            <h1 className="font-serif text-4xl md:text-5xl text-ink mb-4">Quick policy update</h1>
            <p className="text-ink-soft text-[15px] leading-relaxed max-w-xl mx-auto">
              Welcome back{meta.client_name ? `, ${meta.client_name.split(" ")[0]}` : ""}. We've refreshed our payment terms. This one-page acknowledgement replaces re-doing your full intake — it takes about 30 seconds.
            </p>
          </div>
          <form onSubmit={submitReConsent}>
            <Section icon={ShieldCheck} title="Updated late-fees policy">
              <label className="flex items-start gap-3 cursor-pointer p-4 rounded-xl border border-sand bg-cream hover:border-navy/40 transition-colors" data-testid="reconsent-agree-wrap">
                <input
                  type="checkbox"
                  checked={agreement.agreed_to_late_fees_policy}
                  onChange={(e) => setAgreement({ ...agreement, agreed_to_late_fees_policy: e.target.checked })}
                  data-testid="reconsent-agree"
                  className="mt-1 h-4 w-4 accent-navy"
                />
                <div className="text-sm leading-relaxed text-ink">
                  <strong className="block font-serif text-base text-ink mb-1">I've read the updated payment terms</strong>
                  Payment is due within <strong>14 calendar days (two weeks)</strong> of the session date. Late fees apply if payment isn't made and there's been no communication.
                  <details className="mt-3 group" data-testid="reconsent-details">
                    <summary className="cursor-pointer text-xs text-navy hover:underline list-none">
                      <span className="group-open:hidden">Show full late-fee policy</span>
                      <span className="hidden group-open:inline">Hide policy</span>
                    </summary>
                    <ul className="list-disc ml-5 mt-2 space-y-1 text-ink-soft text-xs">
                      <li><strong>Prior communication</strong> about a late payment: no additional fee.</li>
                      <li><strong>After 2 weeks with no communication:</strong> $50 late fee.</li>
                      <li><strong>After 4 weeks with no communication:</strong> further $100.</li>
                      <li><strong>After 6 weeks:</strong> referred to debt collection, additional $250 fee.</li>
                    </ul>
                    <p className="mt-2 text-xs text-ink-soft italic">If cost is a concern, contact Jesse before 14 days and a payment plan can be arranged — no late fee while a plan is in place.</p>
                  </details>
                </div>
              </label>
              <div className="grid sm:grid-cols-2 gap-4 mt-5">
                <div>
                  <label className={labelCls}>Type your full name as signature</label>
                  <input className={inputCls} required value={agreement.typed_signature} onChange={(e) => setAgreement({ ...agreement, typed_signature: e.target.value })} data-testid="reconsent-signature" />
                </div>
                <div>
                  <label className={labelCls}>Today's date</label>
                  <input className={inputCls} type="date" required value={agreement.signed_on} onChange={(e) => setAgreement({ ...agreement, signed_on: e.target.value })} />
                </div>
              </div>
            </Section>
            <button type="submit" disabled={submitting} data-testid="reconsent-submit" className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-navy text-cream px-6 py-4 text-base font-medium hover:bg-navy-deep transition disabled:opacity-60">
              {submitting ? <><Loader2 size={16} className="animate-spin" /> Submitting…</> : "Submit acknowledgement"}
            </button>
          </form>
        </div>
      </div>
    );
  }


  return (
    <div className="min-h-screen bg-cream px-4 md:px-6 py-10">
      <Toaster richColors position="top-center" />
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-8">
          <div className="text-[11px] uppercase tracking-[0.28em] text-ink-soft mb-3">A Mindful Space Counselling</div>
          <h1 className="font-serif text-4xl md:text-5xl text-ink mb-4">Welcome.</h1>
          <p className="text-ink-soft text-[15px] leading-relaxed max-w-xl mx-auto">
            We are so pleased you have taken this courageous step to begin your counselling journey with us. The information below helps us understand your unique background and current needs. Everything you share is held in the strictest confidence. Please take your time and answer honestly.
          </p>
        </div>

        <form onSubmit={submit}>
          <Section icon={ClipboardList} title="Part 1 — Your details" subtitle="For administrative and emergency purposes only.">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Full legal name</label>
                <input className={inputCls} required value={intake.full_legal_name} onChange={(e) => setIntake({ ...intake, full_legal_name: e.target.value })} data-testid="intake-full-name" />
              </div>
              <div>
                <label className={labelCls}>Preferred name</label>
                <input className={inputCls} value={intake.preferred_name} onChange={(e) => setIntake({ ...intake, preferred_name: e.target.value })} />
              </div>
              <div>
                <label className={labelCls}>Date of birth</label>
                <input className={inputCls} type="date" required value={intake.date_of_birth} onChange={(e) => setIntake({ ...intake, date_of_birth: e.target.value })} />
              </div>
              <div>
                <label className={labelCls}>Phone number</label>
                <input className={inputCls} required value={intake.phone} onChange={(e) => setIntake({ ...intake, phone: e.target.value })} />
              </div>
              <div className="sm:col-span-2">
                <label className={labelCls}>Email</label>
                <input className={inputCls} type="email" required value={intake.email} onChange={(e) => setIntake({ ...intake, email: e.target.value })} />
              </div>
              <div className="sm:col-span-2">
                <label className={labelCls}>Current address</label>
                <textarea className={`${inputCls} resize-y`} rows={2} required value={intake.address} onChange={(e) => setIntake({ ...intake, address: e.target.value })} />
              </div>
              <div className="sm:col-span-2">
                <label className={labelCls}>Occupation</label>
                <input className={inputCls} required value={intake.occupation} onChange={(e) => setIntake({ ...intake, occupation: e.target.value })} />
              </div>
            </div>
          </Section>

          <Section icon={Heart} title="Emergency contact" subtitle="This person will ONLY be contacted if we have serious concerns about your immediate safety or wellbeing.">
            <div className="grid sm:grid-cols-3 gap-4">
              <div>
                <label className={labelCls}>Name</label>
                <input className={inputCls} required value={intake.emergency_contact.name} onChange={(e) => setIntake({ ...intake, emergency_contact: { ...intake.emergency_contact, name: e.target.value } })} />
              </div>
              <div>
                <label className={labelCls}>Relationship</label>
                <input className={inputCls} required value={intake.emergency_contact.relationship} onChange={(e) => setIntake({ ...intake, emergency_contact: { ...intake.emergency_contact, relationship: e.target.value } })} />
              </div>
              <div>
                <label className={labelCls}>Phone</label>
                <input className={inputCls} required value={intake.emergency_contact.phone} onChange={(e) => setIntake({ ...intake, emergency_contact: { ...intake.emergency_contact, phone: e.target.value } })} />
              </div>
            </div>
          </Section>

          <Section icon={ClipboardList} title="Part 2 — Current concerns & goals" subtitle="We want to know what brought you here today and what you hope to achieve.">
            <div>
              <label className={labelCls}>What are the primary reasons you are seeking counselling at this time?</label>
              <textarea className={`${inputCls} resize-y`} rows={4} required value={intake.primary_reasons} onChange={(e) => setIntake({ ...intake, primary_reasons: e.target.value })} />
            </div>
            <div>
              <label className={labelCls}>How long have you been experiencing these difficulties?</label>
              <textarea className={`${inputCls} resize-y`} rows={2} required value={intake.duration} onChange={(e) => setIntake({ ...intake, duration: e.target.value })} />
            </div>
            <div>
              <label className={labelCls}>What are your goals for our work together?</label>
              <textarea className={`${inputCls} resize-y`} rows={3} required value={intake.goals} onChange={(e) => setIntake({ ...intake, goals: e.target.value })} />
            </div>
            <div>
              <label className={labelCls}>On a scale of 1 (not at all distressed) to 10 (highest distress), how would you rate your current level of distress?</label>
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                {Array.from({ length: 10 }).map((_, i) => {
                  const n = i + 1;
                  const active = intake.distress_level === n;
                  return (
                    <button
                      type="button"
                      key={n}
                      onClick={() => setIntake({ ...intake, distress_level: n })}
                      data-testid={`distress-${n}`}
                      className={`h-10 w-10 rounded-full border text-sm font-medium transition-colors ${active ? "bg-navy text-cream border-navy" : "bg-cream text-ink-soft border-sand hover:border-navy/50"}`}
                    >
                      {n}
                    </button>
                  );
                })}
              </div>
            </div>
          </Section>

          <Section icon={Heart} title="Part 3 — Health and wellness background" subtitle="Understanding your overall wellness helps us contextualise your experiences.">
            <div>
              <label className={labelCls}>Have you ever been diagnosed with a mental health condition? If yes, please list the condition(s) and approximate date of diagnosis.</label>
              <textarea className={`${inputCls} resize-y`} rows={3} required value={intake.mental_health_history} onChange={(e) => setIntake({ ...intake, mental_health_history: e.target.value })} />
            </div>
            <div>
              <label className={labelCls}>Have you been in counselling or therapy before? If yes, please list approximate dates, type of therapy, and reason for ending.</label>
              <textarea className={`${inputCls} resize-y`} rows={3} required value={intake.prior_therapy} onChange={(e) => setIntake({ ...intake, prior_therapy: e.target.value })} />
            </div>
            <div>
              <label className={labelCls}>Are you currently taking any prescription medications? If yes, please list them.</label>
              <textarea className={`${inputCls} resize-y`} rows={3} required value={intake.medications} onChange={(e) => setIntake({ ...intake, medications: e.target.value })} />
            </div>
            <div>
              <label className={labelCls}>Are you currently receiving treatment from any other healthcare professionals? (e.g. Psychiatrist, GP, Physiotherapist, Dietician.)</label>
              <textarea className={`${inputCls} resize-y`} rows={3} required value={intake.other_treatment} onChange={(e) => setIntake({ ...intake, other_treatment: e.target.value })} />
            </div>
            <div>
              <label className={labelCls}>Please note any significant past or current physical health issues. (e.g. chronic pain, heart conditions, recent surgeries.)</label>
              <textarea className={`${inputCls} resize-y`} rows={3} required value={intake.physical_health} onChange={(e) => setIntake({ ...intake, physical_health: e.target.value })} />
            </div>
            <div>
              <label className={labelCls}>Do you use alcohol or recreational drugs? If yes, please indicate how often and to what extent.</label>
              <textarea className={`${inputCls} resize-y`} rows={3} required value={intake.substance_use} onChange={(e) => setIntake({ ...intake, substance_use: e.target.value })} />
            </div>
          </Section>

          <Section icon={ShieldCheck} title="Counselling agreement" subtitle="Please read each section carefully and tick to confirm.">
            <div className="space-y-4">
              <label className="flex items-start gap-3 cursor-pointer p-4 rounded-xl border border-sand bg-cream hover:border-navy/40 transition-colors">
                <input
                  type="checkbox"
                  checked={agreement.agreed_to_collaboration}
                  onChange={(e) => setAgreement({ ...agreement, agreed_to_collaboration: e.target.checked })}
                  data-testid="agree-collaboration"
                  className="mt-1 h-4 w-4 accent-navy"
                />
                <div className="text-sm leading-relaxed text-ink">
                  <strong className="block font-serif text-base text-ink mb-1">My commitment to growth and collaboration</strong>
                  I look forward to working alongside Jesse on this journey. Together, we will gently explore the issues I bring forward, focusing on growth and moving past challenges. Jesse will offer guidance, helping me clarify my issues and goals. I understand that true progress often requires work outside of sessions, and I am prepared to apply myself to any agreed-upon exercises. I accept that I am ultimately responsible for the decisions I make in my life and how I behave within those spaces.
                </div>
              </label>

              <label className="flex items-start gap-3 cursor-pointer p-4 rounded-xl border border-sand bg-cream hover:border-navy/40 transition-colors">
                <input
                  type="checkbox"
                  checked={agreement.agreed_to_process}
                  onChange={(e) => setAgreement({ ...agreement, agreed_to_process: e.target.checked })}
                  data-testid="agree-process"
                  className="mt-1 h-4 w-4 accent-navy"
                />
                <div className="text-sm leading-relaxed text-ink">
                  <strong className="block font-serif text-base text-ink mb-1">Understanding the counselling process</strong>
                  I appreciate that this model of counselling is comprehensive and holistic. It thoughtfully examines both my present life and my past experiences, considering my upbringing, feelings, communication patterns, and thoughts. The core goal is to foster wholeness and gently guide me back toward my optimum self.
                </div>
              </label>

              <label className="flex items-start gap-3 cursor-pointer p-4 rounded-xl border border-sand bg-cream hover:border-navy/40 transition-colors">
                <input
                  type="checkbox"
                  checked={agreement.agreed_to_confidentiality}
                  onChange={(e) => setAgreement({ ...agreement, agreed_to_confidentiality: e.target.checked })}
                  data-testid="agree-confidentiality"
                  className="mt-1 h-4 w-4 accent-navy"
                />
                <div className="text-sm leading-relaxed text-ink">
                  <strong className="block font-serif text-base text-ink mb-1">Confidentiality and care</strong>
                  Everything we discuss will be treated with the utmost confidentiality. I acknowledge the important professional limits:
                  <ul className="list-disc ml-5 mt-2 space-y-1 text-ink-soft">
                    <li>Any information relating to serious harm to myself or others.</li>
                    <li>Matters concerning children at risk of abuse.</li>
                    <li>Information required for disclosure in a court of law.</li>
                    <li>General information that may be discussed during professional supervision to ensure the highest quality of care.</li>
                  </ul>
                </div>
              </label>

              <label className="flex items-start gap-3 cursor-pointer p-4 rounded-xl border border-sand bg-cream hover:border-navy/40 transition-colors">
                <input
                  type="checkbox"
                  checked={agreement.agreed_to_late_fees_policy}
                  onChange={(e) => setAgreement({ ...agreement, agreed_to_late_fees_policy: e.target.checked })}
                  data-testid="agree-late-fees"
                  className="mt-1 h-4 w-4 accent-navy"
                />
                <div className="text-sm leading-relaxed text-ink">
                  <strong className="block font-serif text-base text-ink mb-1">Session fee, payment terms & late fees</strong>
                  I understand that booking a session is an agreement to pay the full fee for the service. Payment is due within <strong>14 calendar days (two weeks)</strong> of the session date, and I've read (or will read) the full late-fee policy below.
                  <details className="mt-3 group" data-testid="intake-late-fees-details">
                    <summary className="cursor-pointer text-xs text-navy hover:underline list-none">
                      <span className="group-open:hidden">Show full late-fee policy</span>
                      <span className="hidden group-open:inline">Hide policy</span>
                    </summary>
                    <ul className="list-disc ml-5 mt-2 space-y-1 text-ink-soft text-xs">
                      <li><strong>Prior communication</strong> about a late payment (and payment made when communicated): <em>no additional fee</em> — total remains the session fee.</li>
                      <li><strong>After 2 weeks with no communication or attempt to pay:</strong> a <strong>$50</strong> late fee is applied.</li>
                      <li><strong>After 4 weeks with no communication or proof of attempted payment:</strong> a further <strong>$100</strong> is applied.</li>
                      <li><strong>After 6 weeks:</strong> the account is referred to <strong>debt collection</strong>, incurring an additional <strong>$250</strong> debt-collection fee.</li>
                    </ul>
                    <p className="mt-2 text-xs text-ink-soft italic">
                      If you're experiencing financial difficulty, please reach out to Jesse <em>before</em> the 14-day window closes — a payment plan can be arranged and no late fee is applied while an agreed plan is in place.
                    </p>
                  </details>
                </div>
              </label>

              <div className="grid sm:grid-cols-2 gap-4 mt-5">
                <div>
                  <label className={labelCls}>Type your full name as your signature</label>
                  <input
                    className={`${inputCls} font-serif italic text-lg`}
                    required
                    value={agreement.typed_signature}
                    onChange={(e) => setAgreement({ ...agreement, typed_signature: e.target.value })}
                    placeholder="Your name"
                    data-testid="agree-signature"
                  />
                </div>
                <div>
                  <label className={labelCls}>Date</label>
                  <input
                    className={inputCls}
                    type="date"
                    required
                    value={agreement.signed_on}
                    onChange={(e) => setAgreement({ ...agreement, signed_on: e.target.value })}
                  />
                </div>
              </div>
            </div>
          </Section>

          <div className="text-center pb-12">
            <button
              type="submit"
              disabled={submitting}
              data-testid="intake-submit"
              className="inline-flex items-center gap-2 rounded-full bg-navy text-cream px-9 py-4 text-base font-medium hover:bg-navy-deep transition-colors disabled:opacity-60"
            >
              {submitting ? <Loader2 className="animate-spin" size={18} /> : <Check size={18} />}
              Submit my forms
            </button>
            <p className="text-xs text-ink-soft mt-4">
              By submitting you confirm the information is accurate and you agree to the counselling agreement above.
            </p>
          </div>
        </form>
      </div>
    </div>
  );
}
